import { useState } from "react"
import { Link } from "react-router-dom"
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Inbox } from "lucide-react"
import { toast } from "sonner"
import { Badge } from "@/components/ui/badge"
import { BACKEND_API_URL, authHeaders } from "@/lib/api"
import { useReadOnly } from "@/lib/read-only"

const PAGE_SIZE = 50
const STATUSES = ["open", "seen", "done"] as const
const KINDS = ["bug", "feature_request", "experience", "feedback", "feedback_ask"] as const
const SENTIMENTS = ["positive", "mixed", "negative"] as const
const WINDOWS = [7, 30, 90] as const

type ReportStatus = (typeof STATUSES)[number]
type ReportKind = (typeof KINDS)[number]
type Sentiment = (typeof SENTIMENTS)[number]

interface MemberReport {
  id: string
  user_id: string
  member_first_name: string | null
  kind: ReportKind
  sentiment: Sentiment | null
  title: string
  details: string
  member_quote: string | null
  chat_run_id: string | null
  status: ReportStatus
  created_at: string
  updated_at: string
}

interface MemberReportsPage {
  reports: MemberReport[]
  total: number
  limit: number
  offset: number
}

const KIND_LABELS: Record<ReportKind, string> = {
  bug: "Bug",
  feature_request: "Feature request",
  experience: "Experience",
  feedback: "Feedback",
  feedback_ask: "Asked",
}

const KIND_VARIANTS: Record<ReportKind, "destructive" | "default" | "secondary" | "outline"> = {
  bug: "destructive",
  feature_request: "default",
  experience: "secondary",
  feedback: "outline",
  feedback_ask: "outline",
}

const SENTIMENT_STYLES: Record<Sentiment, string> = {
  positive: "bg-green-100 text-green-800",
  mixed: "bg-amber-100 text-amber-800",
  negative: "bg-red-100 text-red-800",
}

interface FeedbackSummary {
  days: number
  truncated: boolean
  feedback: Record<Sentiment, number>
  positive_followed_by_sent_share: number
}

async function getMemberReports(status: string, kind: string, offset: number): Promise<MemberReportsPage> {
  const params = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(offset) })
  if (status) params.set("status", status)
  if (kind) params.set("kind", kind)
  const response = await fetch(`${BACKEND_API_URL}/admin/member_reports?${params}`, {
    headers: await authHeaders(),
  })
  if (!response.ok) throw new Error("Failed to load member reports")
  return response.json()
}

async function getFeedbackSummary(days: number): Promise<FeedbackSummary> {
  const response = await fetch(`${BACKEND_API_URL}/admin/member_reports/feedback_summary?days=${days}`, {
    headers: await authHeaders(),
  })
  if (!response.ok) throw new Error("Failed to load the feedback summary")
  return response.json()
}

async function setMemberReportStatus(id: string, status: ReportStatus): Promise<MemberReport> {
  const response = await fetch(`${BACKEND_API_URL}/admin/member_reports/${id}`, {
    method: "PATCH",
    headers: await authHeaders(),
    body: JSON.stringify({ status }),
  })
  if (!response.ok) throw new Error("Failed to update the report")
  return (await response.json()).report
}

function formatWhen(iso: string): string {
  return new Date(iso).toLocaleString("en-US", {
    month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
  })
}

function StatusToggle({ report }: { report: MemberReport }) {
  const { readOnly } = useReadOnly()
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: (status: ReportStatus) => setMemberReportStatus(report.id, status),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["member-reports"] }),
    onError: (error: Error) => toast.error(error.message),
  })
  return (
    <div className="inline-flex rounded-md border" role="group" aria-label="Report status">
      {STATUSES.map((status) => (
        <button
          key={status}
          type="button"
          disabled={readOnly || mutation.isPending}
          title={readOnly ? "Read-only mode" : undefined}
          aria-pressed={report.status === status}
          onClick={() => !readOnly && report.status !== status && mutation.mutate(status)}
          className={`px-2.5 py-1 text-xs capitalize first:rounded-l-md last:rounded-r-md disabled:cursor-not-allowed disabled:opacity-50 ${
            report.status === status ? "bg-primary text-primary-foreground" : "hover:bg-muted"
          }`}
        >
          {status}
        </button>
      ))}
    </div>
  )
}

function ReportRow({ report }: { report: MemberReport }) {
  return (
    <li className="space-y-2 border-b px-4 py-3 last:border-b-0">
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant={KIND_VARIANTS[report.kind]}>{KIND_LABELS[report.kind]}</Badge>
        {report.sentiment && (
          <span className={`rounded px-1.5 py-0.5 text-xs font-medium capitalize ${SENTIMENT_STYLES[report.sentiment]}`}>
            {report.sentiment}
          </span>
        )}
        <span className="font-medium">{report.title}</span>
        <span className="ml-auto text-xs text-muted-foreground">{formatWhen(report.created_at)}</span>
      </div>
      <p className="whitespace-pre-wrap text-sm">{report.details}</p>
      {report.member_quote && (
        <blockquote className="border-l-2 pl-3 text-sm italic text-muted-foreground">
          "{report.member_quote}"
        </blockquote>
      )}
      <div className="flex flex-wrap items-center gap-3 text-xs">
        <Link to={`/users/${report.user_id}`} className="text-primary hover:underline">
          {report.member_first_name || "Member"} · {report.user_id.slice(0, 8)}
        </Link>
        <Link
          to={`/chat-transcripts?user=${report.user_id}&name=${encodeURIComponent(report.member_first_name ?? "")}`}
          className="text-muted-foreground hover:text-foreground hover:underline"
        >
          Conversation
        </Link>
        <div className="ml-auto">
          <StatusToggle report={report} />
        </div>
      </div>
    </li>
  )
}

function LoopSummary() {
  const [days, setDays] = useState<number>(30)
  const { data, error } = useQuery({
    queryKey: ["member-reports-feedback-summary", days],
    queryFn: () => getFeedbackSummary(days),
  })
  const tiles: Array<[string, number | undefined]> = [
    ["Positive", data?.feedback.positive],
    ["Mixed", data?.feedback.mixed],
    ["Negative", data?.feedback.negative],
    ["Positive, then contact card sent", data?.positive_followed_by_sent_share],
  ]
  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="mb-3 flex items-center gap-3">
        <h2 className="font-semibold">Feedback loop</h2>
        <select
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          className="rounded-md border bg-background px-2 py-1 text-sm"
          aria-label="Window"
        >
          {WINDOWS.map((w) => (
            <option key={w} value={w}>Last {w} days</option>
          ))}
        </select>
        {data?.truncated && <span className="text-xs text-muted-foreground">Counting the newest 1000 only</span>}
      </div>
      {error ? (
        <p className="text-sm text-red-700">{(error as Error).message}</p>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {tiles.map(([label, value]) => (
            <div key={label} className="rounded-md bg-muted/40 px-3 py-2">
              <div className="text-xs text-muted-foreground">{label}</div>
              <div className="text-xl font-semibold">{value ?? "-"}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function FilterSelect({
  label, value, options, onChange,
}: {
  label: string
  value: string
  options: readonly string[]
  onChange: (value: string) => void
}) {
  return (
    <label className="flex items-center gap-2 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="rounded-md border bg-background px-2 py-1 text-sm"
      >
        <option value="">All</option>
        {options.map((option) => (
          <option key={option} value={option}>
            {KIND_LABELS[option as ReportKind] ?? option}
          </option>
        ))}
      </select>
    </label>
  )
}

export function MemberReportsPage() {
  const [status, setStatus] = useState("open")
  const [kind, setKind] = useState("")
  const [offset, setOffset] = useState(0)
  const { data, isLoading, error } = useQuery({
    queryKey: ["member-reports", status, kind, offset],
    queryFn: () => getMemberReports(status, kind, offset),
    placeholderData: keepPreviousData,
  })

  const changeFilter = (setter: (value: string) => void) => (value: string) => {
    setter(value)
    setOffset(0)
  }

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-bold">
          <Inbox className="size-6" /> Member Reports
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Bugs, feature requests, rough experiences and feedback the agent took to the team on a member's behalf.
        </p>
      </div>

      <LoopSummary />

      <div className="flex flex-wrap items-center gap-4">
        <FilterSelect label="Status" value={status} options={STATUSES} onChange={changeFilter(setStatus)} />
        <FilterSelect label="Kind" value={kind} options={KINDS} onChange={changeFilter(setKind)} />
        {data && <span className="ml-auto text-sm text-muted-foreground">{data.total} total</span>}
      </div>

      <div className="rounded-lg border bg-card">
        {isLoading ? (
          <div className="flex h-32 items-center justify-center">
            <div className="inline-block h-6 w-6 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          </div>
        ) : error || !data ? (
          <div className="p-6 text-red-700">Failed to load member reports: {(error as Error)?.message}</div>
        ) : data.reports.length === 0 ? (
          <div className="p-6 text-sm text-muted-foreground">No reports match these filters.</div>
        ) : (
          <ul>
            {data.reports.map((report) => (
              <ReportRow key={report.id} report={report} />
            ))}
          </ul>
        )}
      </div>

      {data && (data.total > PAGE_SIZE || offset > 0) && (
        <div className="flex items-center justify-end gap-2 text-sm">
          <button
            type="button"
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            className="rounded-md border px-3 py-1 disabled:opacity-50"
          >
            Previous
          </button>
          <span className="text-muted-foreground">
            {offset + 1}-{Math.min(offset + PAGE_SIZE, data.total)} of {data.total}
          </span>
          <button
            type="button"
            disabled={offset + PAGE_SIZE >= data.total}
            onClick={() => setOffset(offset + PAGE_SIZE)}
            className="rounded-md border px-3 py-1 disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}
    </div>
  )
}
